<template>
  <v-row class="h-100 flex-md-nowrap" no-gutters data-cy="channel-details">
    <tree-navigation :key="item.id" :tree-id="item.id" :country-code="getMarket" />
    <v-col>
      <breadcrumb-nav :items="breadcrumbs" />
      <nv-page :header="item.name">
        <template slot="toolbar">
          <v-spacer />
          <nv-btn type="back" />
          <v-btn
            v-if="$auth.hasPermissions('owl.marketview')"
            color="accent"
            data-cy="full-transfer-btn"
            @click="transferForm.show = true"
          >
            <v-icon class="mr-1">mdi-transfer</v-icon>
            <span>Full Transfer</span>
          </v-btn>
          <nv-btn
            v-if="item.status != 'archived' && $auth.hasPermissions('owl.marketview')"
            type="edit"
            @click="form.show = true"
          />
          <v-btn
            v-if="item.status == 'archived' && $auth.hasPermissions('owl')"
            color="#ff9800"
            @click="reactivateDialogue()"
          >
            <v-icon class="mr-1">mdi-update</v-icon>
            <span>Reactivate</span>
          </v-btn>
        </template>
        <v-card class="pa-4" data-cy="details-card">
          <v-row dense>
            <nv-show cols="12" lg="1" md="4" sm="6" header="Level">Channel</nv-show>
            <nv-show ref="name" cols="12" lg="3" md="4" sm="6" header="Name">
              {{ item.name }}
              <mav-badge v-if="showMavBadge && item.created_via_mav" />
            </nv-show>
            <nv-show cols="12" lg="1" md="4" sm="6" header="Tree ID">{{ item.id }}</nv-show>
            <nv-show ref="parent" cols="12" lg="3" md="4" sm="6" header="Parent">
              {{ item.brand.parent.name }}
            </nv-show>
            <nv-show ref="brand" cols="12" lg="2" md="4" sm="6" header="Brand">
              {{ item.brand.name }}
            </nv-show>
            <nv-show ref="market" cols="12" lg="2" md="4" sm="6" header="Market">
              <CountryBadge :country="getMarket" />
            </nv-show>
            <nv-show ref="status" cols="12" lg="1" md="4" sm="6" header="Status">
              {{ item.status | humanize }}
            </nv-show>
            <nv-show cols="12" lg="7" md="12" sm="6" header="VCIDs" no-placeholder>
              <vcid-pane
                v-if="cids"
                :value="item.id"
                :no-add="!cids.length"
                :cids="cids"
                @saved="fetchData"
              />
            </nv-show>
            <nv-show ref="category" cols="12" lg="2" md="4" sm="6" header="Category">
              {{ item.sub_category ? item.sub_category.category?.name : null }}
            </nv-show>
            <nv-show ref="subcategory" cols="12" lg="2" md="4" sm="6" header="Subcategory">
              {{ item.sub_category ? item.sub_category.name : null }}
            </nv-show>
          </v-row>
        </v-card>

        <v-card class="mt-4 elevation-2" data-cy="tabs-card">
          <v-tabs v-model="tabs" slider-color="accent">
            <v-tab data-cy="apps-tab"> Applications </v-tab>
            <v-tab data-cy="localization-tab"> Relabel </v-tab>
            <v-tab data-cy="history-tab"> History </v-tab>
            <v-tab data-cy="patterns-tab"> Rules </v-tab>
          </v-tabs>
          <v-tabs-items v-if="item.id" v-model="tabs">
            <v-tab-item data-cy="apps-pane">
              <app-dictionary-tab :child-id="item.id" :classifiable="item.classifyable" />
            </v-tab-item>
            <v-tab-item data-cy="localization-pane">
              <localization-tab-list :id="item.id" ref="localization-tab-list" type="channels" />
            </v-tab-item>
            <v-tab-item data-cy="history-pane">
              <proposals-history
                :id="item.id"
                ref="history-list"
                type="children"
                :country-code="getMarket"
              />
            </v-tab-item>
            <v-tab-item data-cy="patterns-pane">
              <patterns-tab
                :id="item.id"
                ref="patterns-list"
                :classifiable="item.classifyable"
                kind="tree"
                :country-code="getMarket"
              />
            </v-tab-item>
          </v-tabs-items>
        </v-card>

        <brand-channel-form
          v-model="form.show"
          :editing="item"
          channel
          :country-code="getMarket"
          @saved="fetchData"
        />
        <transfer-form v-if="item.id" v-model="transferForm.show" :child-id="item.id" />
        <nv-confirm ref="confirm" />
      </nv-page>
    </v-col>
  </v-row>
</template>

<script>
import HTTP from '@/api/owl';
import ProposalsHistory from '@/components/mv/ProposalsHistory';
import BrandChannelForm from '@/components/mv/BrandChannelForm';
import VcidPane from '@/components/mv/VcidPane';
import TreeNavigation from '@/components/mv/TreeNavigation';
import BreadcrumbNav from '@/components/mv/BreadcrumbNav';
import AppDictionaryTab from '@/components/mv/AppDictionaryTab';
import LocalizationTabList from '@/components/mv/parents/LocalizationTabList';
import PatternsTab from '@/components/mv/PatternsTab.vue';
import TransferForm from '@/components/mv/TransferForm';
import CountryBadge from '@/components/CountryBadge';
import MavBadge from '@/components/MavBadge';
import { getConfigMap } from '@/helpers/filters';

export default {
  components: {
    TransferForm,
    LocalizationTabList,
    AppDictionaryTab,
    BreadcrumbNav,
    TreeNavigation,
    VcidPane,
    BrandChannelForm,
    ProposalsHistory,
    PatternsTab,
    CountryBadge,
    MavBadge,
  },
  data: () => ({
    item: {
      id: null,
      brand: {
        parent: {},
      },
    },
    tabs: null,
    form: {
      show: false,
      edit: null,
    },
    breadcrumbs: [],
    filtersParam: 'chan.ft',
    cids: null,
    transferForm: {
      show: false,
    },
  }),
  computed: {
    getMarket() {
      return this.$route.query.countryCode == '*' ? 'Default' : this.$route.query.countryCode;
    },
    showMavBadge() {
      const config = getConfigMap.call(this);
      return config['mavFeature_owl_ui']?.enabled?.mav_badge === true;
    },
  },
  created() {
    this.fetchData();
  },
  methods: {
    fetchCids(parentId) {
      HTTP.get(`/parents/${parentId}/cids`, { params: { per_page: 100 } }).then(({ data }) => {
        this.cids = data;
      });
    },
    fetchData() {
      let params = { country_code: this.$route.query.countryCode };
      HTTP.get(`/channels/${this.$route.params.childId}`, { params }).then(({ data }) => {
        this.item = data;
        this.fetchCids(data.brand.parent.id);
        if (data.brand) {
          this.breadcrumbs = [
            {
              text: data.brand.parent.name,
              disabled: false,
              to: {
                name: 'owl.parents.show',
                params: {
                  parentId: data.brand.parent.id,
                },
                query: {
                  countryCode: this.$route.query.countryCode,
                },
              },
            },
            {
              text: data.brand.name,
              disabled: false,
              to: {
                name: 'owl.brands.show',
                params: {
                  childId: data.brand.id,
                },
                query: {
                  countryCode: this.$route.query.countryCode,
                },
              },
            },
            {
              text: data.name,
              disabled: true,
            },
          ];
        }
      });
    },
    reactivateDialogue() {
      this.$refs.confirm
        .open(`You are trying to reactivate the ${this.item.type} "${this.item.name}"`)
        .then(() => {
          HTTP.put(`/admin/reactivate/${this.item.type}`, { id: this.item.id })
            .then(() => {
              this.$flash('success', 'Successful');
            })
            .catch(err => {
              this.$flash('error', err.toString());
            });
        });
    },
  },
};
</script>
